// Copyright 2019 Fastcastcity Inc. All Rights Reserved.

/**
 * @fileoverview Interface with functions that make REST endpoint calls to fastcast-service
 *
 * @author sabir.shrestha@gmail.com (Sabir Shrestha)
 */

const axios = require('axios');
const qs = require('qs');

const defaultConfig = require('./config.js');


class FastcastService {

    constructor(config) {
        this.config = config || defaultConfig;
    }
    /**
     *
     * @param {String} username
     * @param {String} password
     *
     * @return {Promise<Object>}
     */
    async getToken(username, password) {
        return axios.post(
            `${this.config.fastcast.authBaseUrl}/auth/realms/dev/protocol/openid-connect/token`, qs.stringify({
                username: username,
                password: password,
                grant_type: 'password',
                client_id: 'fastcast-app'
            })
        );
    }

    /**
     * Retrieves all clients accessible with the accessToken
     *
     * @param {String} accessToken
     *
     * @return {Promise<Object>} client object
     **/
    async getClientByBusinessKey(accessToken, businessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients/${businessKey}`
        };

        return axios(options);
    }

    /**
     * Retrieves all clients accessible with the accessToken
     *
     * @param {String} accessToken
     *
     * @return {Promise<Object>} Object with array of clients
     **/
    async getClients(accessToken) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients`
        };

        return axios(options);
    }

    /**
     * Creates a clients accessible with the accessToken
     *
     * @param {String} accessToken
     *
     * @param {String} clientBK
     * @return {Promise<Object>} newly created client object
     **/
    async deleteClient(accessToken, clientBK) {
        const options = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients/${clientBK}`
        };

        return axios(options);
    }

    /**
     * Creates a clients accessible with the accessToken
     *
     * @param {String} accessToken
     *
     * @param {String} name
     * @param {String} details
     * @param {String} address
     * @param {String} website
     * @return {Promise<Object>} newly created client object
     **/
    async postClient(accessToken, name, details, address, website, metadata) {
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients`,
            data: {
                name,
                details,
                address,
                website,
                metadata,
                active: true
            }
        };

        return axios(options);
    }

    /**
     * Update the client
     *
     * @param {String} accessToken
     *
     * @return {Promise<Object>} Object with array of clients
     **/
    async updateClient(accessToken, client) {
        const options = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients/${client.businessKey}`,
            data: {
                name: client.name,
                details: client.details,
                address: client.address,
                website: client.website,
                metadata: client.metadata,
                active: true
            }
        };

        return axios(options);
    }

    /**
     * Retrieves all projects that user has access to
     *
     * @param {String} accessToken
     *
     * @return {Promise<AxiosResponse<any>>}
     */
    async getUserProjects(accessToken) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects`
        };
        return axios(options);
    }
    /**
     * Retrieves all projects linked to the client
     *
     * @param {String} accessToken
     * @param {String} clientBusinessKey
     *
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async getClientProjects(accessToken, clientBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients/${clientBusinessKey}/projects`
        };

        return axios(options);
    }

    /**
     * Retrieves all users linked to the client
     *
     * @param {String} accessToken
     * @param {String} clientBusinessKey
     *
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async getClientUsers(accessToken, clientBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/clients/${clientBusinessKey}/users`
        };

        return axios(options);
    }

    /**
     * Adds the calculation for fan computation to the queue
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     * @param {String} projectBusinessKey
     * @param {String} clientBusinessKey
     * @returns {Promise<>}
     */
    async queueFanCalculation(accessToken, calculationBusinessKey, projectBusinessKey, clientBusinessKey, maxHeight) {
        const postData = {
            clientKey: clientBusinessKey,
            projectKey: projectBusinessKey,
            maxHeight
        };

        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: postData,
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/queue-fan`
        };

        return axios(options);
    }

    /**
     * Adds the calculation for net new computation to the queue
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     * @param {String} projectBusinessKey
     * @param {String} clientBusinessKey
     * @param {Boolean} useOriginalInputArtifact
     * @returns {Promise<>}
     */
    async queueNetNewCalculation(accessToken, calculationBusinessKey, projectBusinessKey, clientBusinessKey, useOriginalInputArtifact=false) {
        const postData = {
            clientKey: clientBusinessKey,
            projectKey: projectBusinessKey,
            useOriginalInputArtifact
        };

        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: postData,
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/queue-net-new`
        };

        return axios(options);
    }

    /**
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     * @param {String} clientBusinessKey
     * @returns {Promise<>}
     */
    async getNetNewArtifacts(accessToken, calculationBusinessKey, clientBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/net-new-artifacts`,
            params: {clientBusinessKey}
        };

        return axios(options);
    }
    /**
     * Creates user linked to the client
     *
     * @param {String} accessToken
     * @param {String} clientBusinessKey
     * @param {String} userBusinessKey
     * @param {String} userRole
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async postClientUser(accessToken, clientBusinessKey, userBusinessKey, userRole) {
        const postData = {
            clientBusinessKey,
            userKey: userBusinessKey,
            userRole,
            projectBusinessKeys: []
        };
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: postData,
            url: `${this.config.fastcast.serviceBaseUrl}/api/users`
        };

        return axios(options);
    }

    /**
     * Deletes user linked to the client
     *
     * @param {String} accessToken
     * @param {String} userBusinessKey
     *
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async deleteClientUser(accessToken, userBusinessKey) {
        const options = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/users/${userBusinessKey}`
        };

        return axios(options);
    }

    /**
     * Retrieves all users linked to the project
     *
     * @param {String} accessToken
     * @param {String} projectBusinessKey
     *
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async getProjectUsers(accessToken, projectBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects/${projectBusinessKey}/users`
        };

        return axios(options);
    }

    /**
     * Creates user linked to the project
     *
     * @param {String} accessToken
     * @param {String} projectBusinessKey
     * @param {String} userBusinessKey
     * @param {String} userRole
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async postProjectUser(accessToken, projectBusinessKey, userBusinessKey, userRole) {
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: {
                'projectBusinessKey': projectBusinessKey,
                'userKey': userBusinessKey,
                'userRole': userRole
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects/${projectBusinessKey}/users`
        };

        return axios(options);
    }

    /**
     * Deletes user linked to the project
     *
     * @param {String} accessToken
     * @param {String} projectBusinessKey
     * @param {String} userBusinessKey
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async deleteProjectUser(accessToken, projectBusinessKey, userBusinessKey) {
        const options = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects/${projectBusinessKey}/users/${userBusinessKey}`
        };

        return axios(options);
    }
    /**
     * Retrieves all calculations linked to the project
     *
     * @param {String} accessToken
     * @param {String} projectBusinessKey
     *
     * @return {Promise<Object>} Object with array of calculations
     **/
    async getCalculations(accessToken, projectBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations`,
            params: {
                projectBusinessKey
            }
        };

        return axios(options);
    }

    /**
     * Retrieves DXF file linked to the calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     *
     * @return {Promise<Object>} Object with calculation DXF
     **/
    async getCalculationDxf(accessToken, calculationBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/artifact-output`
        };

        return axios(options);
    }

    /**
     * Retrieves DXF file linked to the calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     *
     * @return {Promise<Object>} Object with calculation DXF
     **/
    async getCalculationInputDxf(accessToken, calculationBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/artifact-input`
        };

        return axios(options);
    }

    /**
     * Retrieves DXF file linked to the calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     *
     * @return {Promise<Object>} Object with calculation DXF
     **/
    async getCalculationOutputDxf(accessToken, calculationBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/artifact-output`
        };

        return axios(options);
    }

    /**
     * Creates a new calculation
     *
     * @param {String} accessToken
     * @param {String} projectBusinessKey
     * @param {String} calculationName
     * @param {String} maxHeight
     * @param {String} metadata - Stringified JSON to include:
     *                parcelId, cityCode, centroidHeight, centroidLongitude, centroidLatitude
     *
     * @return {Promise<Object>} Object with calculation
     **/
    async postFanCalculation(accessToken, projectBusinessKey, calculationName, maxHeight, metadata) {
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations?projectBusinessKey=${projectBusinessKey}`,
            data: {
                'name': calculationName,
                'active': true,
                'calculationType': 'FAN',
                'maxHeight': maxHeight,
                'metadata': metadata
            }
        };

        return axios(options);
    }

    /**
     * Updates existing calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     * @param {String} calculationName
     * @param {String} maxHeight
     *
     * @return {Promise<Object>} Object with calculation
     *
     * @version 191018 JPL - Removed data "qs.stringify(...)" to fix an HTTP 415.
     * @version 191018 JPL - Changed warning string.
     **/
    async updateCalculationDxf(accessToken, calculationBusinessKey, calculationName, maxHeight) {
        const options = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}`,
            data: {
                'name': calculationName,
                'active': true,
                'calculationType': 'FAN',
                'maxHeight': maxHeight
            }
        };

        return axios(options);
    }

    /**
     * Deletes existing calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     *
     * @return {Promise<Object>}
     **/
    async deleteCalculation(accessToken, calculationBusinessKey) {
        const options = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}`
        };

        return axios(options);
    }

    /**
     * Attaches DXF file to an existing referenced calculation
     *
     * @param {String} accessToken
     * @param {String} calculationBusinessKey
     * @param {String} parcelDxfString
     *
     * @return {Promise<Object>}
     *
     * @version 191018 JPL - Changed warning string.
     *                       Changed quotes in body to double-quotes to fix http status 500 error.
     **/
    async postParcelDxf(accessToken, calculationBusinessKey, parcelDxfString) {
        // Convert the DXF string to an uploadable (multipart) file.
        const boundary = '---------------------------7da24f2e50046';
        const body = '--' + boundary + '\r\n'
                + 'Content-Disposition: form-data; name="file";'
                + 'filename="temp.txt"\r\n'
                + 'Content-type: plain/text\r\n\r\n'
                + parcelDxfString + '\r\n'
                + '--'+ boundary + '--';

        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': `multipart/form-data; boundary=${boundary}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/calculations/${calculationBusinessKey}/artifact`,
            data: body
        };

        return axios(options);
    }

    /**
     * Retrieves all clients linked to the user
     *
     * @param {String} accessToken
     * @param {String} userBusinessKey
     *
     * @return {Promise<Object>} Object with array of clientProjects
     **/
    async getUserClients(accessToken, userBusinessKey) {
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/users/${userBusinessKey}`
        };

        return axios(options);
    }

    /**
     * Creates a new project
     *
     * @param accessToken
     * @param clientBusinessKey
     * @param projectName
     * @returns {Promise<>}
     */
    async postProject(accessToken, clientBusinessKey, projectName, projectType) {
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: {
                clientBusinessKey: clientBusinessKey,
                active: true,
                name: projectName,
                projectType
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects`
        };

        return axios(options);
    }

    /**
     *
     * @param accessToken
     * @param projectBK
     * @returns {Promise<>}
     */
    async deleteProject(accessToken, projectBK) {
        const options = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            url: `${this.config.fastcast.serviceBaseUrl}/api/projects/${projectBK}`
        };

        return axios(options);
    }
};

module.exports = FastcastService;
